import { API } from '@/redux/config/index';
import { useSiteId } from '@/utils/hooks/useCustomerId';
import { raiseErrorException } from '@/utils/raiseErrorException';
import { toast } from 'react-toastify';
import { StnAPI } from '../config/customHeadersApi';
import {
  setAllOnBoardingTemplates,
  setAllOptions,
  setAllQuestionnaires,
  setAllQuestions,
  setAllSubQuestions,
  setCustomerOnBoardingTemplate,
  setEscalationPath,
  setIsLoading,
  setIsLoadingRemediationRequest,
  setOnBoardingFormsForCustomer,
  setPossibleSollutionForCustomerQuesries,
  setReducerError,
  setResponseForOnBoardingForm,
  setSelectedQuestion,
  setSollutionsForQuestion,
  start,
  startLoadingPossibleSollutionForCustomerQueries,
  startQuestionnaire,
  stopLoadingPossibleSollutionForCustomerQueries,
} from '../reducers/onboardingReducer';

// Questionnaire CRUD Actions
export const getAllQuestionnaires =
	(isActive = true) =>
	async dispatch => {
		try {
			dispatch(startQuestionnaire());
			const response = await API.get(`/onboarding/form?isActive=${isActive}`);
			dispatch(setAllQuestionnaires(response?.data));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'get');
		}
	};

export const createQuestionnaire =
	(data, setModal, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			const response = await API.post(`/onboarding/form`, data);
			if (response?.status === 200) {
				toast.success('Questionnaire created successfully');
				dispatch(getAllQuestionnaires(isActive));
				setModal(false);
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const updateQuestionnaire =
	(onboardingFormID, data, setModal, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			await API.put(`/onboarding/form/${onboardingFormID}`, data);
			dispatch(getAllQuestionnaires(isActive));
			toast.success('Questionnaire updated successfully');
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const deleteQuestionnaire =
	(onboardingFormID, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			await API.post(`/onboarding/form/${onboardingFormID}/archive`);
			dispatch(getAllQuestionnaires(isActive));
			toast.success('Questionnaire Archived successfully');
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

// Questions CRUD Actions
export const getAllQuestions =
	(onboardingFormID, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			const response = await API.get(
				`/onboarding/form/${onboardingFormID}/question?isActive=${isActive}`,
			);
			dispatch(setAllQuestions(response?.data));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'get');
		}
	};

export const getQuestionByID = questionID => async dispatch => {
	try {
		dispatch(start());
		const response = await API.get(`/onboarding/question/${questionID}`);
		dispatch(setSelectedQuestion(response?.data));
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'get');
	}
};

export const getAllSubQuestions = questionID => async dispatch => {
	try {
		dispatch(start());
		const response = await API.get(
			`/onboarding/question/${questionID}/subQuestions`,
		);
		dispatch(setAllSubQuestions(response?.data));
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'get');
	}
};
export const createQuestion =
	(onboardingFormID, data, setModal, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			await API.post(`/onboarding/form/${onboardingFormID}/question`, data);
			dispatch(getAllQuestions(onboardingFormID, isActive));
			toast.success('Question created successfully');
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const updateQuestion =
	(onboardingFormID, questionID, data, setModal, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			await API.put(`/onboarding/question/${questionID}`, data);
			if (onboardingFormID) {
				dispatch(getAllQuestions(onboardingFormID, isActive));
			}
			toast.success('Question updated successfully');
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const updateQuestionPositions =
	(onboardingFormID, data, setModal, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/form/${onboardingFormID}/question/reorder`,
				data,
			);
			dispatch(getAllQuestions(onboardingFormID, isActive));
			toast.success('Questions positions updated successfully');
			if (setModal) {
				setModal(false);
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};
export const updateSubQuestionPositions =
	(questionID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/question/${questionID}/subQuestion/reorder`,
				data,
			);
			dispatch(getAllSubQuestions(questionID));
			toast.success('Sub Questions positions updated successfully');
			if (setModal) {
				setModal(false);
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const deleteQuestion =
	(onboardingFormID, questionID, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			await API.post(`/onboarding/question/${questionID}/archive`);
			dispatch(getAllQuestions(onboardingFormID, isActive));
			toast.success('Question Archived successfully');
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const deleteSubQuestion =
	(questionID, subQuestionID) => async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/question/${questionID}/subQuestion/${subQuestionID}/archive`,
			);
			dispatch(getAllSubQuestions(questionID));
			toast.success('Sub Question Archived successfully');
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

// Options CRUD Actions
export const getAllOptions =
	(questionID, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			const response = await API.get(
				`/onboarding/question/${questionID}/options?isActive=${isActive}`,
			);
			dispatch(setAllOptions(response?.data));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'get');
		}
	};

export const createOption = (questionID, data, setModal) => async dispatch => {
	try {
		dispatch(start());
		await API.post(`/onboarding/question/${questionID}/option/add`, data);
		dispatch(getAllOptions(questionID));
		toast.success('Option created successfully');
		setModal(false);
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'post');
	}
};

export const updateOption =
	(questionID, optionID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.put(
				`/onboarding/question/${questionID}/option/${optionID}`,
				data,
			);
			dispatch(getAllOptions(questionID));
			toast.success('Option updated successfully');
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

// Sub Question Options CRUD Actions
export const getAllSubQuestionOptions =
	(subQuestionID, isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			const response = await API.get(
				`/onboarding/subQuestion/${subQuestionID}/options?isActive=${isActive}`,
			);
			dispatch(setAllOptions(response?.data));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'get');
		}
	};

export const createSubQuestionOption =
	(subQuestionID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/subQuestion/${subQuestionID}/options/add`,
				data,
			);
			dispatch(getAllSubQuestionOptions(subQuestionID));
			toast.success('Option created successfully');
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const updateSubQuestionOption =
	(subQuestionID, optionID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.put(
				`/onboarding/subQuestion/${subQuestionID}/option/${optionID}`,
				data,
			);
			dispatch(getAllSubQuestionOptions(subQuestionID));
			toast.success('Option updated successfully');
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const updateSubQuestionOptionPositions =
	(subQuestionID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/subQuestion/${subQuestionID}/options/reorder`,
				data,
			);
			dispatch(getAllSubQuestionOptions(subQuestionID));
			toast.success('Options positions updated successfully');
			if (setModal) {
				setModal(false);
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const deleteSubQuestionOption =
	(subQuestionID, optionID) => async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/subQuestion/${subQuestionID}/option/${optionID}/archive`,
			);
			dispatch(getAllSubQuestionOptions(subQuestionID));
			toast.success('Option Archived successfully');
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

// Sub Questions CRUD Actions
export const updateSubQuestion =
	(questionID, subQuestionID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.put(
				`/onboarding/question/${questionID}/subQuestion/${subQuestionID}`,
				data,
			);
			dispatch(getAllSubQuestions(questionID));
			toast.success('Sub Question updated successfully');
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const updateOptionPositions =
	(questionID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.post(`/onboarding/question/${questionID}/option/reorder`, data);
			dispatch(getAllOptions(questionID));
			toast.success('Options positions updated successfully');
			if (setModal) {
				setModal(false);
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const deleteOption = (questionID, optionID) => async dispatch => {
	try {
		dispatch(start());
		await API.post(
			`/onboarding/question/${questionID}/option/${optionID}/archive`,
		);
		dispatch(getAllOptions(questionID));
		toast.success('Option Archived successfully');
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'post');
	}
};

export const getCustomerOnBoardingForms = customerID => async dispatch => {
	try {
		dispatch(start());
		const API_URL = customerID ? StnAPI : API;
		const response = await API_URL.get(`/onboarding/customer/forms`, {
			headers: {
				'x-customer-id': customerID,
			},
		});
		dispatch(setOnBoardingFormsForCustomer(response?.data));
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'get');
	}
};

export const getResponseForOnBoardingForm =
	(onboardingFormID, avoidLoader = false) =>
	async dispatch => {
		try {
			if (!avoidLoader) {
				dispatch(start());
			}
			const response = await API.get(
				`/onboarding/form/${onboardingFormID}/responses`,
			);

			dispatch(setResponseForOnBoardingForm(response?.data));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'get');
		}
	};

export const submitResponsesForOnBoardingForm =
	(onboardingFormID, customerID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			const API_URL = customerID ? StnAPI : API;
			await API_URL.post(`/onboarding/form/${onboardingFormID}/submit`, data, {
				headers: {
					'x-customer-id': customerID,
					'x-site-id': useSiteId(),
				},
			});
			toast.success('Onboarding Form submitted successfully');
			dispatch(getResponseForOnBoardingForm(onboardingFormID));
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const assignOnBoardingFormToCustomer =
	(onboardingFormID, customerID) => async dispatch => {
		try {
			dispatch(start());
			const API_URL = customerID ? StnAPI : API;
			const response = await API_URL.post(
				`/onboarding/form/${onboardingFormID}/assign`,
				{},
				{
					headers: {
						'x-customer-id': customerID,
						'x-site-id': useSiteId(),
					},
				},
			);
			console.log(response);
			dispatch(getCustomerOnBoardingForms(customerID));
			toast.success('Onboarding Form assigned to Customer successfully');
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};
export const unAssignOnBoardingFormToCustomer =
	(onboardingFormID, customerID) => async dispatch => {
		try {
			dispatch(start());
			const API_URL = customerID ? StnAPI : API;
			await API_URL.delete(
				`/onboarding/form/${onboardingFormID}/unassign?customerID=${customerID}`,
				{},
				{
					headers: {
						'x-customer-id': customerID,
						'x-site-id': useSiteId(),
					},
				},
			);
			dispatch(getCustomerOnBoardingForms(customerID));
			toast.success('Onboarding Form assigned to Customer successfully');
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'delete');
		}
	};

export const completeOnBoardingForm =
	(onboardingFormID, customerID) => async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/form/${onboardingFormID}/complete?customerID=${customerID}`,
			);
			toast.success('Onboarding Form completed successfully');
			dispatch(getCustomerOnBoardingForms(customerID));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const unCompleteOnBoardingForm =
	(onboardingFormID, customerID) => async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/form/${onboardingFormID}/uncomplete?customerID=${customerID}`,
			);
			toast.success('Onboarding Form uncompleted successfully');
			dispatch(getCustomerOnBoardingForms(customerID));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

// Onboarding Template CRUD Actions
export const getAllOnBoardingTemplates =
	(isActive = true) =>
	async dispatch => {
		try {
			dispatch(start());
			const response = await API.get(
				`/onboarding/templates?isActive=${isActive}`,
			);
			dispatch(setAllOnBoardingTemplates(response?.data));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'get');
		}
	};

export const createOnBoardingTemplate = (data, setModal) => async dispatch => {
	try {
		dispatch(start());
		const response = await API.post(`/onboarding/template/create`, data);
		if (response?.status === 200) {
			toast.success('Template created successfully');
			dispatch(getAllOnBoardingTemplates());
			setModal(false);
		}
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'post');
	}
};

export const updateOnBoardingTemplate =
	(templateID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.put(`/onboarding/template/${templateID}`, data);
			dispatch(getAllOnBoardingTemplates());
			toast.success('Template updated successfully');
			setModal(false);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const deleteOnBoardingTemplate = templateID => async dispatch => {
	try {
		dispatch(start());
		await API.post(`/onboarding/template/${templateID}/archive`);
		dispatch(getAllOnBoardingTemplates());
		toast.success('Template Archived successfully');
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'post');
	}
};

export const uploadOnBoardingTemplate =
	(templateID, formData, setModal) => async dispatch => {
		try {
			dispatch(start());
			const response = await API.post(
				`/onboarding/template/${templateID}/upload`,
				formData,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				},
			);
			if (response?.status === 200) {
				toast.success('Template uploaded successfully');
				dispatch(getAllOnBoardingTemplates());
				setModal(false);
			}
		} catch (err) {
			console.log(err);
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const onboardingTemplateForCustomer = customerID => async dispatch => {
	try {
		dispatch(start());
		const response = await API.get(`/onboarding/customer/templates`);
		dispatch(setCustomerOnBoardingTemplate(response?.data));
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'get');
	}
};

export const uploadOnBoardingTemplatesponseOfCustomer =
	(templateID, formData, setModal) => async dispatch => {
		try {
			dispatch(start());
			const response = await API.post(
				`/onboarding/customer/template/${templateID}/upload`,
				formData,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				},
			);
			if (response?.status === 200) {
				toast.success('Template response uploaded successfully');
				dispatch(onboardingTemplateForCustomer());
				setModal(false);
			}
		} catch (err) {
			console.log(err);
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const assignOnBoardingTemplateToCustomer =
	(templateID, customerID) => async dispatch => {
		try {
			dispatch(start());
			const response = await API.post(
				`/onboarding/template/${templateID}/assign`,
				{
					headers: {
						'x-customer-id': customerID,
						'x-site-id': useSiteId(),
					},
				},
			);
			dispatch(onboardingTemplateForCustomer(customerID));
			toast.success('Onboarding Template assigned to Customer successfully');
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const unAssignOnBoardingTemplateToCustomer =
	templateID => async dispatch => {
		try {
			dispatch(start());
			await API.delete(`/onboarding/template/${templateID}/unassign`);
			dispatch(onboardingTemplateForCustomer());
			toast.success(
				'Onboarding Template unassigned from Customer successfully',
			);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'delete');
		}
	};

export const downloadOnBoardingTemplate =
	(templateID, fileID) => async dispatch => {
		try {
			const response = await API.get(
				`/onboarding/customer/template/${templateID}/download?fileID=${fileID}`,
				{
					responseType: 'blob',
				},
			);

			const header = response.headers['content-disposition'];
			let filename = 'downloaded_file';
			if (header) {
				const matches = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (matches != null && matches[1]) {
					filename = matches[1].replace(/['"]/g, '');
				}
			}

			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();

			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'get');
		}
	};

export const getEscalationPath = loaderOff => async dispatch => {
	try {
		if (loaderOff) {
			dispatch(start());
		}
		const response = await API.get(`/esclationPath/settings`);
		dispatch(setEscalationPath(response?.data));
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'get');
	}
};

export const updateEscalationPathInfo = data => async dispatch => {
	try {
		// dispatch(start());
		const response = await API.post(`/esclationPath/settings`, data);
		dispatch(setEscalationPath(response?.data));
		dispatch(getEscalationPath('loaderOff'));
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'post');
	}
};

export const addSubQuestion =
	(questionID, data, setModal) => async dispatch => {
		try {
			dispatch(start());
			await API.post(
				`/onboarding/question/${questionID}/subQuestion/add`,
				data,
			);
			dispatch(getAllSubQuestions(questionID));
			toast.success('Sub Question added to the Question successfully');
			if (setModal) {
				setModal(false);
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const createRemediationRequest =
	(onboardingFormID, data) => async dispatch => {
		try {
			dispatch(start());
			const response = await API.post(
				`/onboarding/customer/form/${onboardingFormID}/help`,
				data,
			);
			toast.success(response?.data);
			dispatch(getResponseForOnBoardingForm(onboardingFormID, true));
			dispatch(setIsLoading(false));
		} catch (err) {
			dispatch(setIsLoadingRemediationRequest(false));
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const addSolutionsToFormQuestions =
	(onboardingFormID, data) => async dispatch => {
		try {
			dispatch(start());
			const response = await API.put(
				`/onboarding/customer/form/${onboardingFormID}/solution`,
				data,
			);
			await dispatch(getResponseForOnBoardingForm(onboardingFormID));
			toast.success(response?.data);
			dispatch(setIsLoading(false));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const updateCustomerQueryAndSolution =
	(questionID, data, setShowUpdateQuery) => async dispatch => {
		try {
			dispatch(start());
			await API.put(`/onboarding/question/${questionID}/solution`, data);
			if (setShowUpdateQuery) {
				setShowUpdateQuery(false);
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const getPossibleSollutionForCustomerQueries =
	questionID => async dispatch => {
		try {
			dispatch(startLoadingPossibleSollutionForCustomerQueries());
			const response = await API.get(
				`/onboarding/question/${questionID}/solution`,
			);
			dispatch(setPossibleSollutionForCustomerQuesries(response?.data));
		} catch (err) {
			dispatch(setReducerError(err));
			dispatch(stopLoadingPossibleSollutionForCustomerQueries());
			raiseErrorException(err, dispatch, setReducerError, 'get');
		}
	};

export const updateSollutionForTheCustomerQuery =
	(
		onboardingFormID,
		questionID,
		data,
		setShowUpdateQuery,
		setShowPreviousResponses,
		toggle,
	) =>
	async dispatch => {
		try {
			await API.put(
				`/onboarding/customer/form/${onboardingFormID}/question/${questionID}/solution`,
				data,
			);
			toast.success('Sollution updated successfully');
			if (setShowUpdateQuery) {
				setShowUpdateQuery(false);
			}
			if (setShowPreviousResponses) {
				setShowPreviousResponses(false);
			}
			if (toggle) {
				toggle();
			}
			dispatch(getResponseForOnBoardingForm(onboardingFormID, true));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};
export const updateQueryForTheCustomer =
	(onboardingFormID, questionID, query) => async dispatch => {
		try {
			await API.put(
				`/onboarding/customer/form/${onboardingFormID}/question/${questionID}/query?query=${query}`,
			);
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const getSollutionsForQuestion = questionID => async dispatch => {
	try {
		dispatch(start());
		const response = await API.get(
			`/onboarding/question/${questionID}/solution`,
		);
		dispatch(setSollutionsForQuestion(response?.data));
	} catch (err) {
		raiseErrorException(err, dispatch, setReducerError, 'get');
	}
};

export const createSollutionForQuestion =
	(questionID, data, toggle) => async dispatch => {
		try {
			dispatch(start());
			await API.post(`/onboarding/question/${questionID}/solution/add`, data);
			dispatch(getSollutionsForQuestion(questionID));
			if (toggle) {
				toggle();
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'post');
		}
	};

export const updateSollutionForQuestion =
	(questionID, solutionID, data, toggle) => async dispatch => {
		try {
			dispatch(start());
			await API.put(
				`/onboarding/question/${questionID}/solution/${solutionID}`,
				data,
			);
			dispatch(getSollutionsForQuestion(questionID));
			if (toggle) {
				toggle();
			}
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'put');
		}
	};

export const deleteSollutionForQuestion =
	(questionID, solutionID) => async dispatch => {
		try {
			dispatch(start());
			await API.delete(
				`/onboarding/question/${questionID}/solution/${solutionID}`,
			);
			dispatch(getSollutionsForQuestion(questionID));
		} catch (err) {
			raiseErrorException(err, dispatch, setReducerError, 'delete');
		}
	};
